import { StreakDay } from '../components/CustomOverlay/Streak/streak.day.interface';

/** 1-based indexes of reward days (days 2, 4, 7). */
export const REWARD_DAYS = [2, 4, 7];

/** 0-based indexes of reward days (days 2, 4, 7 in 1-based). */
export const REWARD_DAY_INDEXES = [1, 3, 6];

/** 0-based indexes of classic (non-reward) days. */
export const CLASSIC_DAY_INDEXES = [0, 2, 4, 5];

export type CurrentDayBackgroundVariant = 'finish' | 'default' | 'none';

export interface StreakDayCellFlags {
  shouldAddPaddingLeft: boolean;
  isRewardDay: boolean;
  isFutureDay: boolean;
  isCompletedNotCurrent: boolean;
  isSpecialDayNotCompleted: boolean;
  isRewardDayNotCompleted: boolean;
  /** Reward day and not completed, excluding current day (for modal). */
  isRewardDayNotCompletedExcludingCurrent: boolean;
  shouldAddBackgroundToCurrent: boolean;
  currentDayBackgroundVariant: CurrentDayBackgroundVariant;
  isClassicDay: boolean;
  isPreviousDay: boolean;
  isDayBeforePrevious: boolean;
  isFirstDay: boolean;
  /** For modal: current day is index 1. */
  isCurrentSecondDay: boolean;
  shouldShiftPreviousForThirdDay: boolean;
  shouldShiftFirstDayFireRight: boolean;
}

/**
 * Computes shared streak day cell flags used by Streak and StreakModal.
 */
export function getStreakDayCellFlags(
  dayLabels: StreakDay[],
  index: number,
  currentDayIndex: number
): StreakDayCellFlags {
  const dayLabel = dayLabels[index];
  const prevDay = index > 0 ? dayLabels[index - 1] : null;

  const isRewardDay = REWARD_DAY_INDEXES.includes(index);
  const isClassicDay = CLASSIC_DAY_INDEXES.includes(index);
  const isFutureDay = !dayLabel.isCompleted && !dayLabel.isCurrent;
  const isCompletedNotCurrent = dayLabel.isCompleted && !dayLabel.isCurrent;
  const isSpecialDayNotCompleted = !dayLabel.isCompleted && !dayLabel.isCurrent && isClassicDay;
  const isRewardDayNotCompleted = isRewardDay && !dayLabel.isCompleted;
  const isRewardDayNotCompletedExcludingCurrent = isRewardDay && !dayLabel.isCompleted && !dayLabel.isCurrent;

  const shouldAddPaddingLeft = index > 0 && !!prevDay?.isCompleted && !dayLabel.isCompleted && dayLabel.isCurrent;
  const shouldAddBackgroundToCurrent = index > 0 && !!prevDay?.isCompleted && dayLabel.isCurrent;
  const isCurrentDayClaimedReward = dayLabel.isCurrent && dayLabel.isCompleted && (isRewardDay || dayLabel.isFinishDay);
  const currentDayBackgroundVariant: CurrentDayBackgroundVariant =
    shouldAddBackgroundToCurrent && (prevDay?.isFinishDay || isCurrentDayClaimedReward)
      ? 'finish'
      : shouldAddBackgroundToCurrent
        ? 'default'
        : 'none';

  const isPreviousDay = index === currentDayIndex - 1 && dayLabel.isCompleted && !dayLabel.isCurrent;
  const isDayBeforePrevious = index === currentDayIndex - 2 && dayLabel.isCompleted && !dayLabel.isCurrent;
  const isFirstDay = index === 0;
  const isCurrentSecondDay = currentDayIndex === 1;
  const shouldShiftPreviousForThirdDay = isPreviousDay && currentDayIndex === 2;
  const shouldShiftFirstDayFireRight = isFirstDay && isPreviousDay && isCurrentSecondDay;

  return {
    shouldAddPaddingLeft,
    isRewardDay,
    isFutureDay,
    isCompletedNotCurrent,
    isSpecialDayNotCompleted,
    isRewardDayNotCompleted,
    isRewardDayNotCompletedExcludingCurrent,
    shouldAddBackgroundToCurrent,
    currentDayBackgroundVariant,
    isClassicDay,
    isPreviousDay,
    isDayBeforePrevious,
    isFirstDay,
    isCurrentSecondDay,
    shouldShiftPreviousForThirdDay,
    shouldShiftFirstDayFireRight,
  };
}

/** Resolve background class from variant (StreakModal uses orange #FFBB7E). */
export function getCurrentDayBackgroundClassModal(variant: CurrentDayBackgroundVariant): string {
  switch (variant) {
    case 'finish':
      return 'bg-custom-yellow-subtlest';
    case 'default':
      return 'bg-[#FFBB7E]';
    default:
      return '';
  }
}

/** Resolve background class from variant (Streak uses theme orange). */
export function getCurrentDayBackgroundClassStreak(variant: CurrentDayBackgroundVariant): string {
  switch (variant) {
    case 'finish':
      return 'bg-custom-yellow-subtlest';
    case 'default':
      return 'bg-custom-orange-default';
    default:
      return '';
  }
}
